import { z } from 'zod';
import { toFlatField } from '../oasis-types';
import { registry } from '../registry';
import { sectionHasValue } from '../util';
/** Section titles used by the Musculoskeletal UI — single source for edit + read-only. */
export const musculoskeletalSectionTitles = {
    rom: 'ROM',
};
const aftercareReplacementOptions = [
    { value: 'anticoagulant-side-effects', label: 'Anticoagulant side effects' },
    { value: 'capillary-refill', label: 'Capillary refill > 3 Sec' },
    { value: 'coolness', label: 'Coolness' },
    { value: 'cyanosis', label: 'Cyanosis' },
    { value: 'inability-to-bear-weight', label: 'Inability to bear weight on operative extremity' },
    { value: 'loss-of-sensation-or-movement', label: 'Loss of sensation or movement' },
    { value: 'pain-increased', label: 'Pain, increased' },
    { value: 'pallor', label: 'Pallor' },
    { value: 'paresthesia', label: 'Paresthesia' },
];
const romOptions = [
    { value: 'WFL', label: 'WFL' },
    { value: 'limited', label: 'Limited' },
];
const locationOptions = [
    { value: 'Left Hip', label: 'Left Hip' },
    { value: 'Right Hip', label: 'Right Hip' },
    { value: 'Both Hips', label: 'Both Hips' },
    { value: 'Left Knee', label: 'Left Knee' },
    { value: 'Right Knee', label: 'Right Knee' },
    { value: 'Both Knees', label: 'Both Knees' },
    { value: 'Left Ankle', label: 'Left Ankle' },
    { value: 'Right Ankle', label: 'Right Ankle' },
    { value: 'Both Ankles', label: 'Both Ankles' },
    { value: 'Left Foot', label: 'Left Foot' },
    { value: 'Right Foot', label: 'Right Foot' },
    { value: 'Both Feet', label: 'Both Feet' },
    { value: 'Left Shoulder', label: 'Left Shoulder' },
    { value: 'Right Shoulder', label: 'Right Shoulder' },
    { value: 'Both Shoulders', label: 'Both Shoulders' },
    { value: 'Left Elbow', label: 'Left Elbow' },
    { value: 'Right Elbow', label: 'Right Elbow' },
    { value: 'Both Elbows', label: 'Both Elbows' },
    { value: 'Left Wrist', label: 'Left Wrist' },
    { value: 'Right Wrist', label: 'Right Wrist' },
    { value: 'Both Wrists', label: 'Both Wrists' },
    { value: 'Left Arm', label: 'Left Arm' },
    { value: 'Right Arm', label: 'Right Arm' },
    { value: 'Both Arms', label: 'Both Arms' },
    { value: 'Left Clavicle', label: 'Left Clavicle' },
    { value: 'Right Clavicle', label: 'Right Clavicle' },
    { value: 'Both Clavicles', label: 'Both Clavicles' },
];
export const baseMusculoskeletalLocationEntry = z.object({
    location: z.string().register(registry, {
        label: 'Location',
        options: [...locationOptions],
    }),
    type: z.string().register(registry, {
        label: 'Type',
        options: [
            { value: 'fracture', label: 'Fracture' },
            { value: 'amputation', label: 'Amputation' },
        ],
    }),
});
export const baseMusculoskeletal = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    musculoskeletalGait: z
        .string()
        .optional()
        .register(registry, {
        label: 'Gait Pattern',
        options: [
            { value: 'normal/baseline', label: 'Normal / Baseline' },
            { value: 'slow/cautious', label: 'Slow, Cautious' },
            { value: 'unsteady', label: 'Unsteady' },
            { value: 'shuffling', label: 'Shuffling' },
            { value: 'ataxic', label: 'Ataxic' },
            { value: 'non-ambulatory', label: 'Non-Ambulatory' },
        ],
    }),
    musculoskeletalStrength: z
        .string()
        .optional()
        .register(registry, {
        label: 'Strength',
        options: [
            { value: 'good', label: 'Good' },
            { value: 'fair', label: 'Fair' },
            { value: 'poor', label: 'Poor' },
        ],
    }),
    ambulatoryAid: z
        .string()
        .optional()
        .register(registry, {
        label: 'Ambulatory Aid',
        options: [
            { value: 'none', label: 'None' },
            { value: 'cane', label: 'Cane' },
            { value: 'walker', label: 'Walker' },
            { value: 'crutches', label: 'Crutches' },
            { value: 'wheelchair', label: 'Wheelchair' },
        ],
    }),
    aftercareHipReplacement: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Aftercare, hip replacement',
        options: [...aftercareReplacementOptions],
    }),
    aftercareKneeReplacement: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Aftercare, knee replacement',
        options: [...aftercareReplacementOptions],
    }),
    autoimmuneCondition: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Autoimmune condition',
        options: [
            { value: 'graves-disease', label: "Graves' Disease" },
            { value: 'lupus', label: 'Lupus' },
            { value: 'myasthenia-gravis', label: 'Myasthenia Gravis' },
            { value: 'multiple-sclerosis', label: 'Multiple Sclerosis' },
            { value: 'psoriasis', label: 'Psoriasis' },
            { value: 'rheumatoid-arthritis', label: 'Rheumatoid Arthritis' },
            { value: 'scleroderma', label: 'Scleroderma' },
            { value: 'sjogrens-syndrome', label: "Sjogren's Syndrome" },
        ],
    }),
    weightBearingRestrictions: z
        .string()
        .optional()
        .register(registry, {
        label: 'Weight bearing restrictions',
        options: [
            { value: 'full', label: 'Full' },
            { value: 'partial', label: 'Partial' },
        ],
    }),
    findings: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Findings',
        options: [
            { value: 'joint-pain', label: 'Joint pain' },
            { value: 'joint-stiffness', label: 'Joint stiffness' },
            { value: 'muscle-weakness', label: 'Muscle weakness' },
            { value: 'poor-balance', label: 'Poor balance' },
            { value: 'high-risk-for-falls', label: 'High risk for falls' },
        ],
    }),
    lueROM: z
        .string()
        .optional()
        .register(registry, {
        label: 'LUE',
        options: [...romOptions],
    }),
    rueROM: z
        .string()
        .optional()
        .register(registry, {
        label: 'RUE',
        options: [...romOptions],
    }),
    lleROM: z
        .string()
        .optional()
        .register(registry, {
        label: 'LLE',
        options: [...romOptions],
    }),
    rleROM: z
        .string()
        .optional()
        .register(registry, {
        label: 'RLE',
        options: [...romOptions],
    }),
    locations: z.array(baseMusculoskeletalLocationEntry).optional(),
    clinicalNarrative: z.string().optional().register(registry, { label: 'MSK / Functional Clinical Narrative' }),
})
    .register(registry, { label: 'Musculoskeletal' });
export const musculoskeletalHasValue = (data) => {
    const { locations, ...clinicalValues } = data;
    const hasLocationValue = locations?.some((location) => sectionHasValue(location));
    return sectionHasValue(clinicalValues) || hasLocationValue;
};
export const baseMusculoskeletalRefined = baseMusculoskeletal.refine((data) => musculoskeletalHasValue(data), {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const SectionGVersioned = {
    musculoskeletal: {
        e1: { alias: 'musculoskeletal', timepoint: ['soc', 'roc', 'rct', 'dsch'] },
    },
    m1800: { e1: { alias: 'm1800', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM'] } },
    m1810: { e1: { alias: 'm1810', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM', 'VBP'] } },
    m1820: { e1: { alias: 'm1820', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM', 'VBP'] } },
    m1830: { e1: { alias: 'm1830', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM', 'VBP'] } },
    m1840: { e1: { alias: 'm1840', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM'] } },
    m1845: { e1: { alias: 'm1845', timepoint: ['soc', 'roc', 'dsch'], tags: ['Q', 'PRA'] } },
    m1850: { e1: { alias: 'm1850', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM'] } },
    m1860: { e1: { alias: 'm1860', timepoint: ['soc', 'roc', 'ofu', 'rct', 'dsch'], tags: ['Q', 'PRA', 'PDGM'] } },
};
export const SectionG = toFlatField(SectionGVersioned);
// G-M1800_CRNT_GROOMING-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to groom self unaided, with or without the use of assistive devices or adapted methods."},{"value":"01","label":"Grooming utensils must be placed within reach before able to complete grooming activities."},{"value":"02","label":"Someone must assist the patient to groom self."},{"value":"03","label":"Patient depends entirely upon someone else for grooming needs."}]
// G-M1810_CRNT_DRESS_UPPER-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to get clothes out of closets and drawers, put them on and remove them from the upper body without assistance."},{"value":"01","label":"Able to dress upper body without assistance if clothing is laid out or handed to the patient."},{"value":"02","label":"Someone must help the patient put on upper body clothing."},{"value":"03","label":"Patient depends entirely upon another person to dress the upper body."}]
// G-M1820_CRNT_DRESS_LOWER-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to obtain, put on, and remove clothing and shoes without assistance."},{"value":"01","label":"Able to dress lower body without assistance if clothing and shoes are laid out or handed to the patient."},{"value":"02","label":"Someone must help the patient put on undergarments, slacks, socks or nylons, and shoes."},{"value":"03","label":"Patient depends entirely upon another person to dress lower body."}]
// G-M1830_CRNT_BATHG-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to bathe self in shower or tub independently, including getting in and out of tub/shower."},{"value":"01","label":"With the use of devices, is able to bathe self in shower or tub independently including getting in and out of the tub/shower."},{"value":"02","label":"Able to bathe in shower or tub with the intermittent assistance of another person:  (a) for intermittent supervision or encouragement or reminders, OR (b) to get in and out of the shower or tub, OR ( c) for washing difficult to reach areas."},{"value":"03","label":"Able to participate in bathing self in shower or tub, but requires presence of another person throughout the bath for assistance or supervision."},{"value":"04","label":"Unable to use the shower or tub, but able to bathe self independently with or without the user of devices at the sink in chair, or on commode."},{"value":"05","label":"Unable to use the shower or tub, but able to participate in bathing self in bed, at the sink, in bedside chair, or on commode, with the assistance or supervision of another person."},{"value":"06","label":"Unable to participate effectively in bathing and is bathed totally by another person."}]
// G-M1840_CRNT_TOILTG-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to get to and from the toilet and transfer independently with or without a device."},{"value":"01","label":"When reminded, assisted, or supervised by another person, able to get to and from the toilet and transfer."},{"value":"02","label":"Unable to get to and from the toilet but is able to use a bedside commode (with or without assistance)."},{"value":"03","label":"Unable to get to and from the toilet or bedside commode but is able to use a bedpan/urinal independently."},{"value":"04","label":"Is totally dependent in toileting."}]
// G-M1845_CRNT_TOILTG_HYGN-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to manage toileting hygiene and clothing management without assistance."},{"value":"01","label":"Able to manage toileting hygiene and clothing management without assistance if supplies/implements are laid out for the patient."},{"value":"02","label":"Someone must help the patient to maintain toileting hygiene and/or adjust clothing."},{"value":"03","label":"Patient depends entirely upon another person to maintain toileting hygiene."}]
// G-M1850_CRNT_TRNSFRNG-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to independently transfer."},{"value":"01","label":"Able to transfer with minimal human assistance or with use of an assistive device."},{"value":"02","label":"Able to bear weight and pivot during the transfer but unable to transfer self."},{"value":"03","label":"Unable to transfer self and is unable to bear weight or pivot when transferred by another person."},{"value":"04","label":"Bedfast, unable to transfer but is able to turn and position self in bed."},{"value":"05","label":"Bedfast, unable to transfer and is unable to turn and position self."}]
// G-M1860_CRNT_AMBLTN-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to independently walk on even and uneven surfaces and negotiate stairs with or without railings (specifically: needs no human assistance or assistive devices)."},{"value":"01","label":"With the use of a one-handed device (for example, cane, single crutch, hemi-walker), able to independently walk on even and uneven surfaces and negotiate stairs with or without railings."},{"value":"02","label":"Requires use of a two-handed device (for example, walker or crutches) to walk alone on a level surface and/or requires human supervision or assistance to negotiate stairs or steps or uneven surfaces."},{"value":"03","label":"Able to walk only with the supervision or assistance of another person at all times."},{"value":"04","label":"Chairfast, unable to ambulate but is able to wheel self independently."},{"value":"05","label":"Chairfast, unable to ambulate and is unable to wheel self."},{"value":"06","label":"Bedfast, unable to ambulate or be up in a chair."}]
